import { useCallback, useEffect, useState } from "react";
import { Empty, Heading, Link, LinkButton, Spinner } from "ui-components";

import ReportAndBlockModal from "@/src/components/modals/ReportAndBlockModal";
import { makeToast } from "@/src/components/Toast/Toast";
import {
  ConnectionResponse,
  ConnectionsDtoStatus,
  useUserAcceptConnection,
  useUserCancelConnection,
  useUserGetMyConnections,
  useUserResendRequestConnection,
} from "@/src/hooks/api/generated";

import { InvitationListCard } from "./InvitationListCard";
import MemberInfoCard from "./MemberInfoCard";

interface IConnectionsListingProps {
  title: string;
  status: ConnectionsDtoStatus;
  threeColumnLayout?: boolean;
  twoColumnLayout?: boolean;
  hideThreeDotsLayout?: boolean;
  listType?: "grid" | "column";
  onUserBlocked?: () => void;
  refetchConnectionCount?: () => void;
}

export function ConnectionsListing({
  title,
  status,
  threeColumnLayout = false,
  twoColumnLayout = false,
  hideThreeDotsLayout = false,
  listType = "grid",
  onUserBlocked,
  refetchConnectionCount,
}: IConnectionsListingProps): JSX.Element {
  const { mutate: fetchMyConnections, status: fetchStatus } =
    useUserGetMyConnections();
  const [connectionsData, setConnectionsData] = useState<ConnectionResponse>();
  const [userIdToBlockAndReport, setUserIdToBlockAndReport] =
    useState<number>();
  const [userIdToReport, setUserIdToReport] = useState<number>();

  const { mutate: userAcceptConnection } = useUserAcceptConnection();
  const { mutate: userResendConnection } = useUserResendRequestConnection();
  const { mutate: userCancelConnection } = useUserCancelConnection();

  const getData = useCallback(() => {
    fetchMyConnections(
      {
        data: {
          status,
        },
      },
      {
        onSuccess: (data) => {
          setConnectionsData(data);
        },
      }
    );
  }, [fetchMyConnections, setConnectionsData, status]);

  useEffect(() => {
    getData();
  }, [status, getData]);

  function handleAcceptInvitation(userId: number): void {
    userAcceptConnection(
      {
        requestedUserId: userId,
      },
      {
        onSuccess: () => {
          getData();
          refetchConnectionCount?.();
          makeToast({
            title: "Request accepted successfully.",
            intent: "success",
          });
        },
        onError: () => {
          makeToast({
            title: "Something went wrong.",
            intent: "danger",
          });
        },
      }
    );
  }

  function handleCancelInvitation(userId: number): void {
    userCancelConnection(
      {
        connectionUserId: userId,
      },
      {
        onSuccess: () => {
          getData();
          refetchConnectionCount?.();
          makeToast({
            title:
              status === ConnectionsDtoStatus.active
                ? "Connection removed successfully."
                : "Request declined successfully.",
            intent: "success",
          });
        },
        onError: () => {
          makeToast({
            title: "Something went wrong.",
            intent: "danger",
          });
        },
      }
    );
  }

  function handleResendConnection(userId: number): void {
    userResendConnection(
      {
        userId,
      },
      {
        onSuccess: () => {
          getData();
          makeToast({
            title: "Your invitation to connect has been resent.",
            intent: "success",
          });
        },
        onError: () => {
          makeToast({
            title: "Something went wrong.",
            intent: "danger",
          });
        },
      }
    );
  }

  const cardOptions = [
    {
      title:
        status === ConnectionsDtoStatus.active
          ? "Remove connection"
          : "Cancel invitation",
      action: handleCancelInvitation,
    },
  ];

  if (status === ConnectionsDtoStatus.request) {
    cardOptions.unshift({
      title: "Resend invitation",
      action: handleResendConnection,
    });
  }

  if (status === ConnectionsDtoStatus.pending) {
    cardOptions.unshift({
      title: "Accept invitation",
      action: handleAcceptInvitation,
    });
  }

  if (status === ConnectionsDtoStatus.active) {
    cardOptions.push({
      title: "Report User",
      action: setUserIdToReport,
    });
    cardOptions.push({
      title: "Block user",
      action: setUserIdToBlockAndReport,
    });
  }

  return (
    <>
      <ReportAndBlockModal
        userId={userIdToBlockAndReport}
        isOpen={!!userIdToBlockAndReport}
        onOpenChange={(value) => {
          if (!value) {
            setUserIdToBlockAndReport(undefined);
          }
        }}
        onUserBlockSuccessfully={() => {
          getData();
          onUserBlocked?.();
        }}
      />
      <ReportAndBlockModal
        userId={userIdToReport}
        isOpen={!!userIdToReport}
        isShowBlock={false}
        onOpenChange={(value) => {
          if (!value) {
            setUserIdToReport(undefined);
          }
        }}
      />

      <div className="flex justify-between items-center">
        <Heading variant="h2">
          {`${title} ${connectionsData ? `(${connectionsData?.total})` : ""}`}
        </Heading>
        <Link href={`/connections/${status}`}>See all</Link>
      </div>
      {connectionsData?.data.length === 0 && (
        <Empty className="mb-5" text={`No ${status} connections.`} />
      )}
      {(fetchStatus === "idle" || fetchStatus === "pending") && (
        <div className="flex justify-center mt-10 w-full">
          <Spinner size="lg" intent="brand-primary" />
        </div>
      )}
      <div
        className={`flex flex-wrap ${
          listType === "grid" ? "" : "flex-col gap-2"
        }`}
      >
        {listType === "grid"
          ? connectionsData?.data.map((connection) => (
              <div
                key={connection.id}
                className={`p-2.5 px-0 md:px-2.5 w-full ${
                  twoColumnLayout
                    ? "md:w-1/2"
                    : threeColumnLayout
                      ? "md:w-1/3"
                      : "md:w-1/5"
                } w-1/2`}
              >
                <MemberInfoCard
                  options={cardOptions}
                  hideThreeDotsLayout={hideThreeDotsLayout}
                  user={connection.connected_user}
                  isShowAllDetails={false}
                />
              </div>
            ))
          : connectionsData?.data.map((connection) => (
              <InvitationListCard
                user={connection.connected_user}
                onAcceptInvitation={() =>
                  handleAcceptInvitation(connection.connected_user.id)
                }
                onRejectInvitation={() =>
                  handleCancelInvitation(connection.connected_user.id)
                }
              />
            ))}
      </div>
      <div className="flex flex-col w-full items-center justify-center">
        <Link
          href={`/connections/${status}`}
          className="text-center mt-2.5 hidden md:block"
        >
          See all
        </Link>
        <LinkButton
          href={`/connections/${status}`}
          intent="secondary"
          className="visible md:hidden"
        >
          See all
        </LinkButton>
      </div>
    </>
  );
}
